import { HorizBox, Pad, PadBox, Separator } from "component/basics";
import { TextField } from "component/text";
import { FormField } from "component/form";
import { CTAButton, SubtleButton, TextButton } from "component/button";
import { colorBorderPrimary, colorSurfacePrimary, colorTextSecondary } from "component/color";
import { Heading } from "component/text";
import { t, TLabel, undefS, undefTL } from "util/stdtypes";
import { Banner } from "component/banner";
import { UtilityText } from "component/text";
import { useHasCapability } from "component/admin";
import { useDatastore, useGlobalProperty, useLogEvent } from "util/datastore";
import { QuestionConfig } from "structure/question";
import { StyleSheet, View } from "react-native";
import { useConfig } from "util/features";
import { WindowTitle } from "component/basics";
import { ConversationScreen } from "component/screen";
import { Question } from "structure/question";
import { useEffect, useState } from "react";
import { Edit as EditIcon, Checkmark as CheckmarkIcon, Close as CloseIcon } from '@carbon/icons-react';
import { RichText } from "component/richtext";

export function EditQuestionScreen() {
    const {topBanners, headerAboveInputWidgets, composerPreview, headerBelowInputWidgets, bottomRecircWidgets, tabs
    } = useConfig<QuestionConfig>();
    useLogEvent('question-edit');
    const name = useGlobalProperty('name');
    const s = EditQuestionScreenStyle;
    const hasCapability = useHasCapability('content/questions');
    if (!hasCapability) {
        return <Banner><UtilityText label={t('You do not have access to this feature')}/></Banner>
    }

    return <View style={s.screen}>
        <WindowTitle label={t('Edit: {name}')} formatParams={{name}} />
        <ConversationScreen>
            <Question {...{name, topBanners, headerAboveInputWidgets, composerPreview, headerBelowInputWidgets, bottomRecircWidgets, tabs}} />
        </ConversationScreen>
        <EditQuestionSidebar />
    </View>
}

const EditQuestionScreenStyle = StyleSheet.create({
    screen: {
        flex: 1,
        flexDirection: 'row',
        backgroundColor: colorSurfacePrimary,
    },
});

function EditQuestionSidebar() {
    const s = EditQuestionSidebarStyle;
    return <View style={s.sidebar}>
        <PadBox horiz={20} vert={20}>
            <Heading type='large' label={t('Edit')} />
        </PadBox>
        <Separator />
        <EditQuestionSidebarSection headingLabel={t('Thread Header')}>
            <EditQuestionName />
        </EditQuestionSidebarSection>
        <Separator />
        <EditQuestionSidebarSection headingText={'Channel'}>
            <UtilityText type='small' text={'Channel picker will be here'} />
        </EditQuestionSidebarSection>
        <Separator />
        <EditQuestionSidebarSection headingText={'Prosocial Format'}>
            <UtilityText type='small' text={'Prosocial format picker will be here'} />
        </EditQuestionSidebarSection>
        <Separator />
        <EditQuestionSidebarSection headingText={'Moderation'}>
            <UtilityText type='small' text={'Moderation settings will be here'} />
        </EditQuestionSidebarSection>
        <Separator />
        <EditQuestionSidebarSection headingText={'Additional Features'}>
            <UtilityText type='small' text={'Additional features will be here'} />
        </EditQuestionSidebarSection>
        <Separator />
        <EditQuestionSidebarSection headingText={'Related Conversations'}>
            <UtilityText type='small' text={'Related conversations will be here'} />
        </EditQuestionSidebarSection>
        <Separator />
        <EditQuestionSidebarSection headingText={'Related Questions'}>
            <UtilityText type='small' text={'Related questions will be here'} />
        </EditQuestionSidebarSection>
    </View>
}

const EditQuestionSidebarStyle = StyleSheet.create({
    sidebar: {
        width: 400,
        borderLeftWidth: 1,
        borderLeftColor: colorBorderPrimary,
    },
});

type EditQuestionSidebarSectionProps = {
    headingLabel?: TLabel;
    headingText?: string;
    children: React.ReactNode;
}

function EditQuestionSidebarSection({
    headingLabel = undefTL,
    headingText = undefS,
    children
}: EditQuestionSidebarSectionProps) {
    return <PadBox horiz={20} vert={20}>
        <Heading type='small' label={headingLabel} text={headingText} weight='medium' />
        <Pad size={32} />
        {children}
    </PadBox>
}

function EditQuestionName() {
    const datastore = useDatastore();
    const name = useGlobalProperty('name');

    const descriptionLabel = t('See the [editorial playbook]({url}) for guidance.');

    return <SaveableTextField
        label={t('Prompt (required)')}
        value={name}
        onSave={(value) =>{
            datastore.setGlobalProperty('name', value);
        }}
        description={<RichText label={descriptionLabel} color={colorTextSecondary} formatParams={{url: '#'}} />}
    />
}

type SaveableTextFieldProps = {
    label: TLabel;
    description?: JSX.Element;
    value: string;
    onSave: (value: string) => void;
}

function SaveableTextField({
    label,
    value,
    onSave,
    description,
}: SaveableTextFieldProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [tempValue, setTempValue] = useState(value);

    useEffect(() => {
        if (!isEditing) {
            setTempValue(value);
        }
    }, [value]);

    const EditButton = () => {
        return <SubtleButton icon={EditIcon} label={t('Edit')} onPress={() => setIsEditing(true)} />
    }

    const handleOnCancel = () => {
        setIsEditing(false);
        setTempValue(value);
    }

    const handleOnSave = () => {
        onSave(tempValue);
        setIsEditing(false);
    }

    return(<FormField
        label={label}
        accessoryAction={isEditing ? <></> : <EditButton />}>
        <TextField
            value={tempValue}
            onChangeText={setTempValue}
            editable={isEditing}
            multiline={false}
        />
        {description && <PadBox top={12}>{description}</PadBox>}
        {isEditing && <PadBox top={12}>
            <HorizBox gap={12} center right>
                <TextButton label={t('Cancel')} onPress={handleOnCancel} color={colorTextSecondary} underline />
                <CTAButton label={t('Save')} onPress={handleOnSave} />
            </HorizBox>
        </PadBox>}
    </FormField>)
}